import React, { useState, useEffect, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Send, X, Loader2, Radio, Megaphone, MessageSquare, Mic } from 'lucide-react';
import { AuthContext } from '../App';
import PTTBroadcastModal from './PTTBroadcastModal';
import PTTWalkieRecorder from './PTTWalkieRecorder';
import { WalkieModeToggle, isWalkieModeOn } from './PTTWalkieListener';
import useClaseEnCurso from '../hooks/useClaseEnCurso';

const OnlinePresenceBar = () => {
  const { API, user: currentUser, getImageUrl } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [showList, setShowList] = useState(false);
  const [actionTarget, setActionTarget] = useState(null); // user tapeado — muestra menú Radio/Ping
  const [pingTarget, setPingTarget] = useState(null); // user a enviarle ping
  const [pingMsg, setPingMsg] = useState('');
  const [pingSending, setPingSending] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [pttStarting, setPttStarting] = useState(false);
  const [walkieTarget, setWalkieTarget] = useState(null); // user para grabar walkie directo
  const listRef = useRef(null);

  const canSendPing = currentUser && (currentUser.rol === 'admin' || currentUser.rol === 'profesor');
  const canBroadcast = canSendPing;
  const canPtt = !!currentUser; // Todos los roles pueden usar PTT en vivo
  const { hayClaseEnCurso } = useClaseEnCurso();

  useEffect(() => {
    const fetchPresence = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API}/users/online-presence`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUsers(data.users || []);
        }
      } catch (e) {}
    };

    fetchPresence();
    const interval = setInterval(fetchPresence, 30000);
    return () => clearInterval(interval);
  }, [API]);

  // Close popup on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (listRef.current && !listRef.current.contains(e.target)) {
        setShowList(false);
      }
    };
    if (showList) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showList]);

  if (users.length === 0) return null;

  const getInitials = (nombre, apellido) => {
    return `${(nombre || '')[0] || ''}${(apellido || '')[0] || ''}`.toUpperCase();
  };

  const openActionMenu = (u) => {
    if (u.status !== 'online') return;
    if (u.id === currentUser?.id) return;
    if (hayClaseEnCurso) {
      toast.warning('🔇 Radio/PTT bloqueado — hay una clase en curso');
      return;
    }
    // Si el Modo Radio está ON, ir directo al walkie recorder (bypass menu)
    if (isWalkieModeOn()) {
      setWalkieTarget(u);
      return;
    }
    setActionTarget(u);
  };

  const openWalkieFromMenu = () => {
    const u = actionTarget;
    if (!u) return;
    if (hayClaseEnCurso) {
      toast.warning('🔇 Radio/PTT bloqueado — hay una clase en curso');
      setActionTarget(null);
      return;
    }
    setActionTarget(null);
    setWalkieTarget(u);
  };

  const openPingFromMenu = () => {
    const u = actionTarget;
    if (!u || !canSendPing) return;
    setActionTarget(null);
    setPingTarget(u);
    setPingMsg('');
  };

  const startPTT = async () => {
    const u = actionTarget;
    if (!u || !canPtt || pttStarting) return;
    if (hayClaseEnCurso) {
      toast.warning('🔇 Radio/PTT bloqueado — hay una clase en curso');
      setActionTarget(null);
      return;
    }
    setPttStarting(true);
    const toastId = toast.loading(`Conectando Radio con ${u.nombre}...`);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/ptt/live-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_user_id: u.id }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'No se pudo iniciar la Radio');
      }
      const data = await resp.json();
      toast.dismiss(toastId);
      // Abrir el iframe local para el iniciador
      window.dispatchEvent(new CustomEvent('iba:open-ptt-call', {
        detail: {
          url: data.url,
          title: `Radio · ${data.target_name}`,
          isPtt: true,
        },
      }));
      setActionTarget(null);
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(e.message || 'Error al iniciar Radio');
    } finally {
      setPttStarting(false);
    }
  };

  const sendPing = async () => {
    if (!pingTarget || !pingMsg.trim()) return;
    setPingSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/pings-rapidos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_user_id: pingTarget.id, mensaje: pingMsg.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || 'Error enviando ping');
      }
      toast.success(`Ping enviado a ${pingTarget.nombre}`);
      setPingTarget(null);
      setPingMsg('');
    } catch (e) {
      toast.error(e.message || 'Error enviando ping');
    } finally {
      setPingSending(false);
    }
  };

  return (
    <div className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-gray-200 px-4 lg:px-8 py-2" data-testid="global-online-presence">
      <div className="max-w-7xl mx-auto">
    <div className="flex items-center gap-1 flex-wrap relative" data-testid="online-presence-bar" ref={listRef}>
      <span className="text-xs text-gray-500 mr-1 font-medium">En línea:</span>
      <div className="flex items-center gap-2 sm:gap-0 sm:-space-x-2 flex-wrap">
        {/* 📢 Megáfono — Broadcast a múltiples (solo admin/profesor).
            Bloqueado cuando hay una clase en curso para no interrumpir. */}
        {canBroadcast && (
          <button
            onClick={() => {
              if (hayClaseEnCurso) {
                toast.warning('🔇 Difusión bloqueada — hay una clase en curso');
                return;
              }
              setShowBroadcast(true);
            }}
            disabled={hayClaseEnCurso}
            className={`relative group cursor-pointer w-11 h-11 sm:w-9 sm:h-9 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white transition mr-2 ${
              hayClaseEnCurso
                ? 'bg-gray-400 opacity-60 cursor-not-allowed'
                : 'bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 hover:ring-2 hover:ring-orange-300'
            }`}
            title={hayClaseEnCurso ? '🔇 Bloqueado durante clase en curso' : 'Difundir nota de voz a varios destinatarios'}
            data-testid="ptt-broadcast-avatar"
          >
            <Megaphone className="w-4 h-4" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              {hayClaseEnCurso ? 'Bloqueado (clase en curso)' : 'Nota de voz · Difundir'}
            </div>
          </button>
        )}
        {users.slice(0, 15).map((u) => {
          const clickable = (canPtt || canSendPing) && u.status === 'online' && !u.out_of_office && u.id !== currentUser?.id;
          return (
          <button
            type="button"
            key={u.id}
            className={`relative group text-left p-0 border-0 bg-transparent ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={clickable ? () => openActionMenu(u) : undefined}
            disabled={!clickable}
            title={clickable ? `Acciones para ${u.nombre}` : `${u.nombre} ${u.apellido || ''}`}
            data-testid={clickable ? `ptt-avatar-${u.id}` : undefined}
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(99,102,241,0.35)' }}
          >
            {u.foto_perfil && u.foto_perfil.trim() ? (
              <>
                <img
                  src={u.foto_perfil}
                  alt={`${u.nombre} ${u.apellido}`}
                  className={`w-11 h-11 sm:w-9 sm:h-9 rounded-full border-2 border-white object-cover shadow-sm ${clickable ? 'hover:ring-2 hover:ring-indigo-400 transition' : ''}`}
                  onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                />
                <div
                  className={`w-11 h-11 sm:w-9 sm:h-9 rounded-full border-2 border-white shadow-sm items-center justify-center text-xs font-bold text-white ${
                    u.rol === 'admin' ? 'bg-blue-500' :
                    u.rol === 'profesor' ? 'bg-orange-500' :
                    'bg-gray-400'
                  } ${clickable ? 'hover:ring-2 hover:ring-indigo-400 transition' : ''}`}
                  style={{ display: 'none' }}
                >
                  {getInitials(u.nombre, u.apellido)}
                </div>
              </>
            ) : (
              <div
                className={`w-11 h-11 sm:w-9 sm:h-9 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-xs font-bold text-white ${
                  u.rol === 'admin' ? 'bg-blue-500' :
                  u.rol === 'profesor' ? 'bg-orange-500' :
                  'bg-gray-400'
                } ${clickable ? 'hover:ring-2 hover:ring-indigo-400 transition' : ''}`}
              >
                {getInitials(u.nombre, u.apellido)}
              </div>
            )}
            {/* Status dot — rojo si está "Fuera de Oficina", verde online, amarillo ausente */}
            <span
              className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white pointer-events-none ${
                u.out_of_office ? 'bg-red-500' :
                u.status === 'online' ? 'bg-green-500' : 'bg-yellow-400'
              }`}
              title={u.out_of_office ? 'Fuera de Oficina — No disponible' : (u.status === 'online' ? 'En línea' : 'Ausente')}
            />
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              {u.nombre} {u.apellido}{u.out_of_office ? ' · Fuera de Oficina' : (clickable && ' · tocar para radio')}
            </div>
          </button>
          );
        })}
      </div>

      {/* Clickable count */}
      <button
        onClick={() => setShowList(!showList)}
        className="ml-2 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors"
        data-testid="online-count-btn"
      >
        {users.length} conectado{users.length !== 1 ? 's' : ''}
      </button>

      {/* Toggle Modo Radio (auto-play walkie) — visible para todos */}
      <div className="ml-auto pl-2">
        <WalkieModeToggle compact />
      </div>

      {/* Popup list */}
      {showList && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 w-72 max-h-80 overflow-y-auto"
          data-testid="online-users-list"
        >
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-semibold text-gray-700 text-sm">Usuarios Conectados</span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{users.length}</span>
          </div>
          <div className="py-1">
            {users.map((u) => {
              const clickable = (canPtt || canSendPing) && u.status === 'online' && !u.out_of_office && u.id !== currentUser?.id;
              return (
              <div
                key={u.id}
                className={`flex items-center gap-3 px-3 py-2 ${clickable ? 'hover:bg-indigo-50 cursor-pointer' : 'hover:bg-gray-50'}`}
                onClick={clickable ? () => { setShowList(false); openActionMenu(u); } : undefined}
                data-testid={clickable ? `ptt-list-${u.id}` : undefined}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {u.foto_perfil && u.foto_perfil.trim() ? (
                    <img
                      src={u.foto_perfil}
                      alt={`${u.nombre} ${u.apellido}`}
                      className="w-8 h-8 rounded-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className={`w-8 h-8 rounded-full items-center justify-center text-xs font-bold text-white ${
                      u.rol === 'admin' ? 'bg-blue-500' :
                      u.rol === 'profesor' ? 'bg-orange-500' :
                      'bg-gray-400'
                    }`}
                    style={{ display: (u.foto_perfil && u.foto_perfil.trim()) ? 'none' : 'flex' }}
                  >
                    {getInitials(u.nombre, u.apellido)}
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                      u.out_of_office ? 'bg-red-500' :
                      u.status === 'online' ? 'bg-green-500' : 'bg-yellow-400'
                    }`}
                  />
                </div>
                {/* Name and role */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{u.nombre} {u.apellido}</p>
                  <p className="text-xs text-gray-500 capitalize">{u.rol}</p>
                </div>
                {/* Status text */}
                <span className={`text-xs font-medium ${u.out_of_office ? 'text-red-600' : u.status === 'online' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {u.out_of_office ? 'No disponible' : (clickable ? '🎙️ Radio' : (u.status === 'online' ? 'Activo' : 'Ausente'))}
                </span>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
      </div>

      {/* Modal de envío de Ping rápido — renderizado vía Portal para escapar del sticky bar */}
      {pingTarget && createPortal(
        <div
          className="fixed inset-0 z-[2147483640] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => !pingSending && setPingTarget(null)}
          data-testid="ping-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-auto overflow-hidden max-h-[calc(100dvh-2rem)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-5 py-3 flex items-center justify-between">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Send className="w-4 h-4" /> Ping rápido
              </h3>
              <button
                onClick={() => setPingTarget(null)}
                disabled={pingSending}
                className="text-white/80 hover:text-white"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="flex items-center gap-3">
                {pingTarget.foto_perfil && pingTarget.foto_perfil.trim() ? (
                  <img
                    src={pingTarget.foto_perfil}
                    alt={pingTarget.nombre}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-green-400"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold ring-2 ring-green-400">
                    {getInitials(pingTarget.nombre, pingTarget.apellido)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{pingTarget.nombre} {pingTarget.apellido}</p>
                  <p className="text-xs text-green-600 font-medium">● En línea</p>
                </div>
              </div>
              <textarea
                value={pingMsg}
                onChange={(e) => setPingMsg(e.target.value.slice(0, 280))}
                placeholder="Ej: 'Vamos a correr un deploy en 2 minutos'"
                rows={3}
                autoFocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    sendPing();
                  }
                }}
                data-testid="ping-textarea"
              />
              <div className="flex items-center justify-between text-[11px] text-gray-500">
                <span>El mensaje aparecerá en pantalla; debe tocar "Aceptar" para cerrarlo.</span>
                <span className={pingMsg.length >= 260 ? 'text-amber-600 font-semibold' : ''}>{pingMsg.length}/280</span>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3 flex items-center justify-end gap-2 border-t shrink-0">
              <button
                onClick={() => setPingTarget(null)}
                disabled={pingSending}
                className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                data-testid="ping-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={sendPing}
                disabled={pingSending || !pingMsg.trim()}
                className="px-4 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                data-testid="ping-send"
              >
                {pingSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {pingSending ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Menú de acciones: Radio en vivo / Ping (al tapear un avatar online) */}
      {actionTarget && createPortal(
        <div
          className="fixed inset-0 z-[2147483639] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !pttStarting && setActionTarget(null)}
          data-testid="ptt-action-menu"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-3 flex items-center gap-3">
              {actionTarget.foto_perfil ? (
                <img
                  src={actionTarget.foto_perfil}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-white/60"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold ring-2 ring-white/60">
                  {getInitials(actionTarget.nombre, actionTarget.apellido)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{actionTarget.nombre} {actionTarget.apellido}</p>
                <p className="text-xs text-white/80 capitalize">● En línea · {actionTarget.rol}</p>
              </div>
              <button
                onClick={() => !pttStarting && setActionTarget(null)}
                className="text-white/80 hover:text-white p-1"
                disabled={pttStarting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {canPtt && (
                <button
                  onClick={openWalkieFromMenu}
                  disabled={pttStarting}
                  className="w-full py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                  data-testid="ptt-walkie-quick"
                >
                  <Mic className="w-5 h-5" />
                  Walkie rápido (hasta 15s)
                </button>
              )}
              {canPtt && (
                <button
                  onClick={startPTT}
                  disabled={pttStarting}
                  className="w-full py-3 bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                  data-testid="ptt-start-live"
                >
                  {pttStarting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Radio className="w-5 h-5" />}
                  {pttStarting ? 'Conectando...' : 'Radio en vivo (bidireccional)'}
                </button>
              )}
              {canSendPing && (
                <button
                  onClick={openPingFromMenu}
                  disabled={pttStarting}
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="ptt-open-ping"
                >
                  <MessageSquare className="w-5 h-5" />
                  Enviar Ping (mensaje)
                </button>
              )}
              <button
                onClick={() => setActionTarget(null)}
                disabled={pttStarting}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Broadcast (nota de voz a múltiples) */}
      <PTTBroadcastModal open={showBroadcast} onClose={() => setShowBroadcast(false)} />

      {/* Modal Walkie rápido — hold-to-record + envío auto */}
      <PTTWalkieRecorder
        open={!!walkieTarget}
        target={walkieTarget}
        onClose={() => setWalkieTarget(null)}
      />
    </div>
  );
};

export default OnlinePresenceBar;
